import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { IUser, UsersService } from '@app/core/users';
import { KeycloakAdminService } from '@app/services/keycloak-admin.service';
import { debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { MsgboxService } from '@app/shared/msgbox';
import { ROLES } from '@app/core/auth';
import { rbac } from '@app/shared/rbac';
import { environment } from 'src/environments/environment';

type UserStatus = 'ok' | 'not-found' | 'error' | 'loading';

@Component({
  selector: 'app-user-detail',
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.css'],
})
export class UserDetailComponent implements OnInit {
  public form: FormGroup;
  public passwordForm: FormGroup;

  public userStatus: UserStatus = 'loading';
  public userKeycloakStatus: UserStatus = 'loading';
  public userServolabStatus: UserStatus = 'loading';

  public saving: boolean;

  isNew: boolean = false;
  public user: IUser;
  public userSisifo: any;

  public displayChangePassword: boolean = false;
  public changeServolabUser = false;
  public isIDRequired = environment.makeIDRequiredInUserCreation || false;

  constructor(
    private service: UsersService,
    private route: ActivatedRoute,
    private keycloakAdminService: KeycloakAdminService,
    private msgBox: MsgboxService,
    private router: Router
  ) {
    this.form = new FormGroup({
      userName: new FormControl('', [Validators.required]),
      role: new FormControl('', [Validators.required]),
      profiles: new FormControl(''),
      email: new FormControl('', [Validators.required, Validators.email]),
      firstName: new FormControl(''),
      lastName: new FormControl(''),
      idCard: new FormControl(''),
      password: new FormControl(''),
      confirmPassword: new FormControl(''),
      sisifoUser: new FormControl(null),
      enabled: new FormControl(true),
    });
  }

  toggleEnabled({ checked }: any): void {
    console.log('toggleEnabled', checked);
    console.log(this.form.value);
    this.service.enableDisableUser(this.user.userName, checked);
  }

  /**
   * ngOnInit
   * @returns
   * @memberof UserDetailComponent
   * @description
   */
  ngOnInit(): void {
    // get the id from the route
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.isNew = true;
      this.form.get('role').setValue(ROLES.User);
      this.form.get('userName').setValidators([Validators.required]);
      this.form.get('userName').updateValueAndValidity();
    } else {
      this.getUserData(+id);
      this.form.get('userName').disable();
    }

    // Reload data when the user name changes to get the data from Keycloak and Sisifo
    this.form
      .get('userName')
      .valueChanges.pipe(
        debounceTime(500), // espera 500ms tras el último cambio
        distinctUntilChanged() // emite solo si el valor cambió
      )
      .subscribe((value) => {
        this.getKeycloakUserData(value);
        this.getSisifoUserData(value);
      });

    // changes on profiles can be update showServolabUser
    this.form
      .get('profiles')
      .valueChanges.pipe(
        debounceTime(500), // espera 500ms tras el último cambio
        distinctUntilChanged() // emite solo si el valor cambió
      )
      .subscribe((value) => {
        this.changeServolabUser = this.showServolabUser(this.user);
        if (this.changeServolabUser) {
          this.getSisifoUserData(this.form.get('userName').value);
        }
      });
  }

  onChangeServolabUser(): void {
    this.changeServolabUser = !this.changeServolabUser;
    const userName = this.form.get('userName').value;
    this.getSisifoUserData(userName);
  }

  /**
   * Get the user data from the server and set the form values
   * @param id
   * @returns
   * @memberof UserDetailComponent
   * @description
   * This method is called when the component is initialized. It gets the user data from the server and sets the form values.
   * It also gets the user data from Keycloak and sets the form values.
   * If the user is not found, it sets the user status to 'not-found'.
   * If there is an error, it sets the user status to 'error'.
   */
  async getUserData(id: number): Promise<void> {
    const user = await firstValueFrom(this.service.get(+id));
    this.user = user;
    this.form.patchValue(user);
    this.userStatus = 'ok';
    const _username = this.form.get('userName').value;
    this.getKeycloakUserData(_username);
    if (this.showServolabUser(user)) {
      this.changeServolabUser = true;
      this.getSisifoUserData(_username);
    }
  }

  showServolabUser(user: IUser): boolean {
    const profiles = this.form.get('profiles')?.value?.split(',') || [];

    // Check if the user has the role of WEBRESULTS
    if (profiles.includes(rbac.profiles.WEBRESULTS_USER)) {
      return true;
    }

    console.log('profiles', profiles);

    console.log('user', user);
    // Check if the user is a Servolab user
    if (user && user.servolabId) {
      return true;
    }

    // Check if the user has role of WEBRESULTS
    if (user && user.profiles) {
      console.log('profiles', user.profiles);
    }
    return false;
  }

  getKeycloakUserData(userName: string): void {
    this.keycloakAdminService.getUserData(userName).subscribe({
      next: (keycloakUser) => {
        this.form.patchValue({
          email: keycloakUser.email,
          firstName: keycloakUser.firstName,
          lastName: keycloakUser.lastName,
          enabled: keycloakUser.enabled,
        });
        this.userKeycloakStatus = 'ok';
      },
      error: (err) => {
        this.form.patchValue({
          email: null,
          firstName: null,
          lastName: null,
        });
        if (err.status === 404) {
          this.userKeycloakStatus = 'not-found';
        } else {
          this.userKeycloakStatus = 'error';
        }
      },
    });
  }

  getSisifoUserData(username: string): void {
    // add centers to the form
    this.service.getSisifoUser(username).subscribe({
      next: (user) => {
        this.userServolabStatus = 'ok';
        // sisifoUser is a SisifoUser object
        this.form.get('sisifoUser').setValue(user);
        if (!this.form.get('email').value) {
          this.form.get('email').setValue(user.eMail);
        }
        if (!this.form.get('firstName').value) {
          this.form.get('firstName').setValue(user.nombre);
        }
        if (!this.form.get('lastName').value) {
          this.form.get('lastName').setValue(user.apellidos);
        }
      },
      error: (error) => {
        this.form.get('sisifoUser').setValue(null);
        if (error.statusCode === 404) {
          this.userServolabStatus = 'not-found';
        } else {
          this.userServolabStatus = 'error';
        }
      },
    });
  }

  /**
   * "PASSWORDS_NOT_MATCHING": "Las contraseñas no coinciden"
   * "PASSWORD_TOO_SHORT": "La contraseña debe tener al menos 8 caracteres"
   * "PASSWORD_TOO_LONG": "La contraseña no puede tener más de 20 caracteres"
   * "PASSWORD_MUST_CONTAIN": "La contraseña debe contener al menos una letra mayúscula, una letra minúscula, un número y un carácter especial"
   * "PASSWORD_MUST_NOT_CONTAIN": "La contraseña no puede contener el nombre de usuario, nombre o apellido"
   * @returns
   */
  validatePassword(): boolean {
    const password: string = this.form.get('password').value;
    const confirmPassword = this.form.get('confirmPassword').value;
    // No validate if the password is empty
    if (password.length + confirmPassword.length < 1) {
      return true;
    }
    if (password !== confirmPassword) {
      this.msgBox.showError('USERS.ERRORS.PASSWORDS_NOT_MATCHING');
      return false;
    } else if (password.length < 8) {
      this.msgBox.showError('USERS.ERRORS.PASSWORD_TOO_SHORT');
      return false;
    } else if (password.length > 20) {
      this.msgBox.showError('USERS.ERRORS.PASSWORD_TOO_LONG');
      return false;
    } else if (!/[a-z]/.test(password)) {
      this.msgBox.showError('USERS.ERRORS.PASSWORD_MUST_CONTAIN');
      return false;
    } else if (!/[A-Z]/.test(password)) {
      this.msgBox.showError('USERS.ERRORS.PASSWORD_MUST_CONTAIN');
      return false;
    } else if (!/[0-9]/.test(password)) {
      this.msgBox.showError('USERS.ERRORS.PASSWORD_MUST_CONTAIN');
      return false;
    } else if (!/[!@#$%^&*]/.test(password)) {
      this.msgBox.showError('USERS.ERRORS.PASSWORD_MUST_CONTAIN');
      return false;
    }
    // Check if the password contains the username or parts of the full name
    const userName = this.form.get('userName').value;
    const firstName = this.form.get('firstName').value;
    const lastName = this.form.get('lastName').value;
    console.log('userName', { userName, password, firstName, lastName });
    if (
      password.includes(userName) ||
      (firstName &&
        firstName.length > 5 &&
        password.toLowerCase().includes(firstName.toLowerCase())) ||
      (lastName &&
        lastName.length > 5 &&
        password.toLowerCase().includes(lastName.toLowerCase()))
    ) {
      this.msgBox.showError('USERS.ERRORS.PASSWORD_MUST_NOT_CONTAIN');
      return false;
    } else {
      return true;
    }
  }

  buildPayload(): any {
    const _payload = this.user || {};

    // const centers = [];
    // if (this.form.get('centers').value) {
    //   if (typeof this.form.get('centers').value === 'string') {
    //     centers.push(+this.form.get('centers').value);
    //   }
    //   if (Array.isArray(this.form.get('centers').value)) {
    //     centers.push(...this.form.get('centers').value.map((c) => +c));
    //   }
    // }
    return {
      ..._payload,
      ...this.form.value,
      // centers,
      changeServolabUser: this.changeServolabUser,
    };
  }

  public saveChanges(): void {
    this.form.markAllAsTouched();
    if (!this.validatePassword()) {
      return;
    }

    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key).markAsDirty();
    });

    if (!this.form.valid) {
      return;
    }

    this.saving = true;

    const op = this.isNew
      ? this.service.createUser(this.buildPayload())
      : this.service.updateUser(this.buildPayload());
    op.subscribe((user) => {
      this.user = user;
      this.saving = false;
      this.router.navigate(['/users']);
    });
  }
}
